import { showGroomingToast } from "@/lib/grooming/toasts";
import { getToolboxBridge } from "./bridge";

/**
 * "Briefs ready" when a batch finishes (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1399; design `.notif`). Inside a contract-2 SpecTree Toolbox
 * whose window is in the background, the app shows an OS notification that
 * opens the project's Session screen. Otherwise (the page is in front, a
 * browser, or a contract-1 app without `notify`) the page shows a toast.
 */

export interface BriefsReadyInput {
  /** Briefs ready to review. */
  count: number;
  project: string;
  /** The session date (`YYYY-MM-DD`), when the agenda has one. */
  sessionDate?: string | null;
  /** "Today" for the date wording; tests pin it. */
  now?: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The session's name in the sentence: "Thursday's session" within the next
 * week, "Thursday, October 30's session" further out, "the next session"
 * without a date. Pure; exported for tests.
 */
export function sessionName(sessionDate: string | null | undefined, now: Date = new Date()): string {
  const match = sessionDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(sessionDate) : null;
  if (!match) return "the next session";
  // Noon UTC keeps a date-only value on the same calendar day in every time zone we format in.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  if (Number.isNaN(date.getTime())) return "the next session";
  const withinWeek = date.getTime() - now.getTime() < 6.5 * DAY_MS;
  const label = date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    weekday: "long",
    ...(withinWeek ? {} : { month: "long", day: "numeric" }),
  });
  return `${label}'s session`;
}

/** The notification's title, body and target. Pure; exported for tests. */
export function briefsReadyMessage(input: BriefsReadyInput): { title: string; body: string; targetPath: string } {
  const items = input.count === 1 ? "1 item" : `${String(input.count)} items`;
  const verb = input.count === 1 ? "is" : "are";
  return {
    title: "Briefs ready",
    body: `${items} for ${sessionName(input.sessionDate, input.now)} ${verb} ready to review.`,
    targetPath: `/grooming/${encodeURIComponent(input.project)}/session`,
  };
}

/**
 * Tell the person their briefs are ready. Returns how: `os` (the app showed a
 * notification) or `toast`. A failed OS notification falls back to the toast.
 */
export async function notifyBriefsReady(input: BriefsReadyInput): Promise<"os" | "toast"> {
  const message = briefsReadyMessage(input);
  const app = getToolboxBridge()?.app;
  if (app?.notify && typeof document !== "undefined" && document.hidden) {
    try {
      await app.notify(message);
      return "os";
    } catch {
      // Fall through to the toast: the person still learns the briefs are ready.
    }
  }
  showGroomingToast(message.body, "notice");
  return "toast";
}
